import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useSelector, useStore } from 'react-redux';
import {
  accountSummary,
  resolveSelectedAccount,
} from '../lib/utils/post_approval_plaid_link';
import {
  capturePostApprovalPlaidEvent,
  POST_APPROVAL_PLAID_EVENTS,
  POST_APPROVAL_PLAID_LINK_EXIT_CODES,
} from '../lib/utils/post_approval_plaid_events';
import usePostApprovalPlaidContext from './usePostApprovalPlaidContext';

const linkError = (code) => ({ code });

const attemptValue = (attempt, key) =>
  attempt && typeof attempt.get === 'function' ? attempt.get(key) : undefined;

const exitCode = (error) =>
  POST_APPROVAL_PLAID_LINK_EXIT_CODES.includes(error?.error_code)
    ? error.error_code
    : 'link_exited';

const isAmbiguousSubmit = (error) =>
  error &&
  (error.network ||
    error.reason_code === 'invalid_response' ||
    error.http_status >= 500);

const usePostApprovalPlaidLink = ({ onAccepted } = {}) => {
  const postApproval = usePostApprovalPlaidContext();
  const store = useStore();
  const sessionId = useSelector((state) =>
    state.sessions.getIn(['session', 'id'])
  );
  const session = useSelector((state) => state.sessions.get('session'));
  const publicKey = useSelector((state) => state.sessions.get('public_key'));
  const applicationId = session?.get('application_id');
  const [token, setToken] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [pendingAccounts, setPendingAccounts] = useState(null);
  const [currentLinkError, setCurrentLinkError] = useState(null);
  const connectionInFlight = useRef(false);
  const linkOpened = useRef(false);
  const submitInFlight = useRef(false);
  const droppedSubmit = useRef(false);
  const transientSelection = useRef(null);
  const submitted = useRef(false);
  const acceptedAttemptId = useRef(null);
  const onAcceptedRef = useRef(onAccepted);
  const mounted = useRef(false);
  const currentContext = useRef({ sessionId, publicKey });
  const linkSessionContext = useRef(null);

  onAcceptedRef.current = onAccepted;
  currentContext.current = { sessionId, publicKey };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const isCurrentContext = useCallback(
    (context) =>
      mounted.current &&
      (!context ||
        (context.sessionId === currentContext.current.sessionId &&
          context.publicKey === currentContext.current.publicKey)),
    []
  );

  const clearTransientSelection = useCallback((selection, updateUi = true) => {
    if (selection && transientSelection.current !== selection) return;
    transientSelection.current = null;
    if (updateUi && mounted.current) setPendingAccounts(null);
  }, []);

  const isCurrentLinkContext = useCallback(
    (context) =>
      Boolean(
        context &&
          linkSessionContext.current === context &&
          !context.consumed &&
          isCurrentContext(context)
      ),
    [isCurrentContext]
  );

  const finishLinkSession = useCallback(() => {
    connectionInFlight.current = false;
    linkOpened.current = false;
    if (mounted.current) {
      setConnecting(false);
      setToken(null);
    }
  }, []);

  const submitAccount = useCallback(
    (account, isRetry = false) => {
      const selection = transientSelection.current;
      if (!mounted.current || !selection || submitInFlight.current) {
        return undefined;
      }

      selection.selectedAccount = account;
      const submission = { selection };
      submitInFlight.current = submission;
      const request = postApproval.submitSelectedAccount(
        selection.publicToken,
        account
      );

      if (request === undefined) {
        if (submitInFlight.current === submission) {
          submitInFlight.current = null;
        }
        if (isRetry) {
          droppedSubmit.current = false;
          setCurrentLinkError(linkError('submit_not_started'));
        } else {
          droppedSubmit.current = true;
        }
        return undefined;
      }

      submitted.current = true;
      capturePostApprovalPlaidEvent(
        POST_APPROVAL_PLAID_EVENTS.SUBMIT_INITIATED,
        {
          session,
          applicationId,
          attempt: postApproval.attempt,
          isRetry,
          accountSelection: selection.accountSelection,
        }
      );
      return Promise.resolve(request)
        .then((result) => {
          clearTransientSelection(
            selection,
            isCurrentContext(selection.context)
          );
          return result;
        })
        .catch((error) => {
          if (isCurrentContext(selection.context)) {
            if (error.reason_code === 'submission_conflict') {
              setCurrentLinkError(linkError('submission_conflict'));
            } else if (isAmbiguousSubmit(error)) {
              setCurrentLinkError(linkError('submission_unknown'));
            }
          }
          if (!isAmbiguousSubmit(error)) {
            clearTransientSelection(
              selection,
              isCurrentContext(selection.context)
            );
          }
          throw error;
        })
        .finally(() => {
          if (submitInFlight.current === submission) {
            submitInFlight.current = null;
          }
        });
    },
    [
      clearTransientSelection,
      isCurrentContext,
      applicationId,
      postApproval.submitSelectedAccount,
      postApproval.attempt,
      session,
    ]
  );

  const chooseAccount = useCallback(
    (index) => {
      const selection = transientSelection.current;
      const account = selection && selection.accounts[index];
      if (!account) return undefined;
      return submitAccount(account);
    },
    [submitAccount]
  );

  const retrySelectedAccount = useCallback(() => {
    const selection = transientSelection.current;
    if (
      !selection ||
      !selection.selectedAccount ||
      !isCurrentContext(selection.context)
    ) {
      return undefined;
    }

    droppedSubmit.current = false;
    setCurrentLinkError(null);
    return submitAccount(selection.selectedAccount, true);
  }, [isCurrentContext, submitAccount]);

  const activeLinkContext = linkSessionContext.current;

  const onSuccess = useCallback(
    (publicToken, metadata) => {
      if (!isCurrentLinkContext(activeLinkContext)) {
        return undefined;
      }
      if (submitInFlight.current) return undefined;
      activeLinkContext.consumed = true;
      finishLinkSession();
      const selection = resolveSelectedAccount(metadata);
      const accounts =
        selection.accounts || (selection.account ? [selection.account] : []);
      capturePostApprovalPlaidEvent(
        POST_APPROVAL_PLAID_EVENTS.LINK_SUCCEEDED,
        {
          session,
          applicationId,
          attempt: postApproval.attempt,
          accountSelection: selection.kind,
          accountCount: accounts.length,
        }
      );
      transientSelection.current = {
        publicToken,
        accounts,
        accountSelection: selection.kind,
        context: activeLinkContext,
      };

      if (selection.kind === 'single') {
        return submitAccount(selection.account);
      }
      if (selection.kind === 'choose') {
        setPendingAccounts(selection.accounts.map(accountSummary));
        return undefined;
      }

      clearTransientSelection();
      setCurrentLinkError(linkError('no_account_selected'));
      return undefined;
    },
    [
      activeLinkContext,
      applicationId,
      clearTransientSelection,
      finishLinkSession,
      isCurrentLinkContext,
      postApproval.attempt,
      session,
      submitAccount,
    ]
  );

  const onExit = useCallback(
    (error) => {
      if (!isCurrentLinkContext(activeLinkContext)) return;
      activeLinkContext.consumed = true;
      capturePostApprovalPlaidEvent(POST_APPROVAL_PLAID_EVENTS.LINK_EXITED, {
        session,
        applicationId,
        attempt: postApproval.attempt,
        exitCode: exitCode(error),
      });
      finishLinkSession();
      clearTransientSelection();
      setCurrentLinkError(
        linkError(
          error && typeof error.error_code === 'string'
            ? error.error_code
            : 'link_exited'
        )
      );
    },
    [
      activeLinkContext,
      applicationId,
      clearTransientSelection,
      finishLinkSession,
      isCurrentLinkContext,
      postApproval.attempt,
      session,
    ]
  );

  const { open, ready } = usePlaidLink({ token, onSuccess, onExit });

  const connect = useCallback(async () => {
    if (!mounted.current || connectionInFlight.current) return undefined;

    connectionInFlight.current = true;
    const requestContext = { ...currentContext.current, consumed: false };
    linkSessionContext.current = requestContext;
    linkOpened.current = false;
    setConnecting(true);
    setCurrentLinkError(null);
    try {
      let linkToken = await postApproval.openLink();
      if (!isCurrentContext(requestContext)) return null;
      const plaidState = store.getState().post_approval_plaid;
      const attempt = plaidState.get('attempt');
      if (
        !linkToken &&
        ((plaidState.get('has_attempt') &&
          attempt.get('status') === 'link_ready') ||
          (postApproval.hasAttempt &&
            attemptValue(postApproval.attempt, 'status') === 'link_ready'))
      ) {
        linkToken = await postApproval.openLink();
        if (!isCurrentContext(requestContext)) return null;
      }

      if (!linkToken || typeof linkToken.link_token !== 'string') {
        finishLinkSession();
        setCurrentLinkError(linkError('link_unavailable'));
        return null;
      }

      setToken(linkToken.link_token);
      return linkToken;
    } catch (error) {
      finishLinkSession();
      setCurrentLinkError(linkError('link_unavailable'));
      return null;
    }
  }, [
    finishLinkSession,
    isCurrentContext,
    postApproval.attempt,
    postApproval.openLink,
    store,
  ]);

  useEffect(() => {
    if (connecting && token && ready && !linkOpened.current) {
      linkOpened.current = true;
      capturePostApprovalPlaidEvent(POST_APPROVAL_PLAID_EVENTS.LINK_OPENED, {
        session,
        applicationId,
        attempt: postApproval.attempt,
      });
      open();
    }
  }, [applicationId, connecting, open, postApproval.attempt, ready, session, token]);

  useEffect(() => {
    if (!postApproval.loading && droppedSubmit.current) {
      droppedSubmit.current = false;
      const selection = transientSelection.current;
      if (
        selection &&
        isCurrentContext(selection.context) &&
        selection.selectedAccount
      ) {
        submitAccount(selection.selectedAccount, true);
      }
    }
  }, [isCurrentContext, postApproval.loading, submitAccount]);

  const attemptId = attemptValue(postApproval.attempt, 'attempt_id');
  const status = attemptValue(postApproval.attempt, 'status');
  const submittedAt = attemptValue(postApproval.attempt, 'submitted_at');

  useEffect(() => {
    if (status === 'processing' || postApproval.attempt?.isTerminal) {
      clearTransientSelection();
      setCurrentLinkError(null);
    }
    if (
      submitted.current &&
      status === 'processing' &&
      submittedAt &&
      attemptId &&
      acceptedAttemptId.current !== attemptId
    ) {
      acceptedAttemptId.current = attemptId;
      onAcceptedRef.current?.(postApproval.attempt);
    }
  }, [
    attemptId,
    clearTransientSelection,
    postApproval.attempt,
    status,
    submittedAt,
  ]);

  useEffect(() => {
    clearTransientSelection();
    droppedSubmit.current = false;
    submitInFlight.current = null;
    submitted.current = false;
    acceptedAttemptId.current = null;
    finishLinkSession();
    setCurrentLinkError(null);
  }, [clearTransientSelection, finishLinkSession, publicKey, sessionId]);

  useEffect(
    () => () => {
      transientSelection.current = null;
      droppedSubmit.current = false;
      submitInFlight.current = null;
      connectionInFlight.current = false;
      linkSessionContext.current = null;
    },
    []
  );

  return {
    connect,
    connecting,
    pendingAccounts,
    chooseAccount,
    retrySelectedAccount,
    linkError: currentLinkError,
    clearLinkError: () => setCurrentLinkError(null),
    attempt: postApproval.attempt,
    phase: postApproval.phase,
    loading: postApproval.loading,
    error: postApproval.error,
    pollingExhausted: postApproval.pollingExhausted,
    retryStatus: postApproval.retryStatus,
    refreshStatus: postApproval.refreshStatus,
    hasAttempt: postApproval.hasAttempt,
  };
};

export default usePostApprovalPlaidLink;
